<template>
    <div class="mb-6 relative">
        <div class="rubrique-title border-t border-b border-gray-200 pb-2 pt-2 tracking-wider">
            <div class="flex items-center gap-px">
                <i class="fa-regular fa-square text-primary text-[13px] mr-[7px] relative"></i>
                Le fil actu
                <span v-if="autoRefresh && showRefreshIndicator" class="ml-2 flex items-center gap-1">
                    <span class="w-1.5 h-1.5 bg-red-600 rounded-full"
                          :class="{ 'opacity-100': loadingDots >= 1, 'opacity-0': loadingDots < 1 }"></span>
                    <span class="w-1.5 h-1.5 bg-red-600 rounded-full"
                          :class="{ 'opacity-100': loadingDots >= 2, 'opacity-0': loadingDots < 2 }"></span>
                    <span class="w-1.5 h-1.5 bg-red-600 rounded-full"
                          :class="{ 'opacity-100': loadingDots >= 3, 'opacity-0': loadingDots < 3 }"></span>
                </span>
            </div>
        </div>
        <label class="cursor-pointer absolute top-[10px] right-0 tooltip tooltip-left hidden"
               data-tip="Activer les notifications">
            <input type="checkbox" class="toggle" />
        </label>
        <div class="news-feed-block h-[380px] mb-4 mt-2.5 relative">
            <div v-if="isLoading"
                 class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 z-10">
                <span class="loading loading-spinner loading-lg text-primary"></span>
            </div>
            <div v-else-if="error"
                 class="absolute inset-0 flex items-center justify-center">
                <div class="text-center">
                    <p class="text-error mb-2">{{ error }}</p>
                    <button @click="loadFeed()" class="btn btn-sm btn-outline">
                        Réessayer
                    </button>
                </div>
            </div>
            <div v-show="!isLoading && !error"
                 class="h-full pr-4 custom-scrollbar"
                 :class="{ 'opacity-0': isLoading, 'opacity-100': !isLoading }">
                <div v-html="feedContent"></div>
            </div>
        </div>

        <div class="text-center mb-5">
            <a class="btn btn-active btn-primary w-3/4 z-4 !font-bold"
               :href="locationPath">
                Voir toutes les actus
            </a>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
    locationPath: {
        type: String,
        required: true
    },
    autoRefresh: {
        type: Boolean,
        default: false
    }
})

const feedContent = ref('')
const isLoading = ref(true)
const error = ref(null)
const showRefreshIndicator = ref(false)
const loadingDots = ref(0)

let refreshInterval = null
let dotsInterval = null

const loadFeed = async () => {
    const isFirstLoad = !feedContent.value
    if (isFirstLoad) {
        isLoading.value = true
    }

    error.value = null
    showRefreshIndicator.value = false

    try {
        const response = await fetch(
            `/api/news-feed`,
            {
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            }
        )

        if (!response.ok) throw new Error('Erreur de chargement')

        const content = await response.text()
        feedContent.value = content
    } catch (err) {
        error.value = err.message
    } finally {
        isLoading.value = false
    }
}

const startAutoRefresh = () => {
    if (!props.autoRefresh) return

    refreshInterval = setInterval(() => {
        showRefreshIndicator.value = true
        loadingDots.value = 0

        if (dotsInterval) {
            clearInterval(dotsInterval)
        }

        // Animer les points un par seconde
        dotsInterval = setInterval(() => {
            loadingDots.value++
            if (loadingDots.value > 3) {
                clearInterval(dotsInterval)
                dotsInterval = null

                // Charger le feed après l'animation
                loadFeed().finally(() => {
                    showRefreshIndicator.value = false
                    loadingDots.value = 0
                })
            }
        }, 1000)
    }, 60000)
}

const stopAutoRefresh = () => {
    if (refreshInterval) {
        clearInterval(refreshInterval)
        refreshInterval = null
    }
    if (dotsInterval) {
        clearInterval(dotsInterval)
        dotsInterval = null
    }
    showRefreshIndicator.value = false
    loadingDots.value = 0
}

onMounted(() => {
    loadFeed()
    if (props.autoRefresh) {
        startAutoRefresh()
    }
})

onUnmounted(() => {
    stopAutoRefresh()
})

defineExpose({ refresh: () => loadFeed() })
</script>

<style scoped>
:deep(.news-feed-placeholder) {
    display: none;
}
</style>
